// hooks/useSetup.js
import { useState } from "react";
import { fetchData } from "../api/client"; // Importando a função de API

// Função que vai lidar com o envio dos dados para cada etapa do setup
const useSetup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Função para enviar os dados da etapa para a API
  const saveStepData = async (etapaAtual, dadosEtapa) => {
    setLoading(true);
    setError(null);

    try {
      // Envia os dados para o backend
      const response = await fetchData("/setup", "POST", {
        etapaAtual,
        dadosEtapa,
      });
      return response?.data ?? response;
    } catch (error) {
      console.error("Erro ao processar a etapa:", error);
      setError(error.message); // Define o erro
      throw error; // Lança o erro para ser tratado no frontend
    } finally {
      setLoading(false); // Fim da requisição
    }
  };

  // Função para gerar sugestões de dados com base no site ou rede social
  const generateSuggestions = async (link) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchData("/setup", "POST", {
        etapaAtual: 0, // Etapa inicial para gerar sugestões
        dadosEtapa: link, // Envia o link como dado
      });

      // console.log(response)
      return response.data; // Retorna os dados sugeridos pela API
      
    } catch (error) {
      console.error("Erro ao gerar sugestões:", error);
      setError(error.message); // Define o erro
      throw error; // Lança o erro para ser tratado no frontend
    } finally {
      setLoading(false); // Fim da requisição
    }
  };

  // Função para gerar sugestões dos níveis de consciência
  const generateAwarenessLevels = async (companyData, options = {}) => {
    if(!loading){
      setLoading(true);
      setError(null);
  
      try {
        const response = await fetchData("/setup", "POST", {
          etapaAtual: 2,
          dadosEtapa: {
            ...companyData,
            awarenessGenerationMode: options.mode || companyData?.awarenessGenerationMode || "hybrid",
          },
        });
  
        return response.data;
      } catch (error) {
        console.error("Erro ao gerar níveis de consciência:", error);
        setError(error.message); // Define o erro
        throw error; // Lança o erro para ser tratado no frontend
      } finally {
        setLoading(false); // Fim da requisição
      }
    }
  };

  // Função para gerar sugestões das etapas de pipeline
  const generatePipelineStages = async (companyData) => {
    if(!loading){
      setLoading(true);
      setError(null);
  
      try {
        const response = await fetchData("/setup", "POST", {
          etapaAtual: 4, // Etapa para gerar pipeline
          dadosEtapa: companyData, // Envia o companyData como dado
        });
  
        return response.data; // Retorna os níveis de consciência sugeridos pela API
      } catch (error) {
        console.error("Erro ao gerar níveis de consciência:", error);
        setError(error.message); // Define o erro
        throw error; // Lança o erro para ser tratado no frontend
      } finally {
        setLoading(false); // Fim da requisição
      }
    }
  };

  // Função para gerar sugestões de tipos/subtipos de solicitações
  const generateRequestTypes = async (companyData) => {
    if (!loading) {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchData("/setup", "POST", {
          etapaAtual: 5, // Etapa para gerar tipos de solicitações
          dadosEtapa: companyData,
        });

        // Compatibilidade com createResponse da API: { data: result, message... }
        return response.data ?? response;
      } catch (error) {
        console.error("Erro ao gerar tipos de solicitações:", error);
        setError(error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  };

  // Função para gerar sugestões de cargos (roles)
  const generateRoles = async (companyData) => {
    if (!loading) {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchData("/setup", "POST", {
          etapaAtual: 8, // Etapa para sugerir cargos
          dadosEtapa: companyData,
        });

        return response.data ?? response;
      } catch (error) {
        console.error("Erro ao gerar cargos sugeridos:", error);
        setError(error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  };

  const commitWizard = async (dadosEtapa) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchData("/setup", "POST", {
        etapaAtual: 12,
        dadosEtapa,
      });
      return response?.data ?? response;
    } catch (e) {
      console.error("Erro ao finalizar setup:", e);
      setError(e?.message || String(e));
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return {
    saveStepData,
    commitWizard,
    generateSuggestions,
    generateAwarenessLevels,
    generatePipelineStages,
    generateRequestTypes,
    generateRoles,
    loading,
    isLoading: loading,
    error,
  };
};

export default useSetup;
